/**
 * Softphone — the phone in the bar (#207 step 2).
 *
 * One trigger at the right of the nav, beside the account menu: a handset
 * with a dot saying whether this browser is registered. Its panel is the
 * house popup (a `relative` wrapper, a panel hung off the bottom-right
 * corner, on a border and never a shadow), and it is one of four screens
 * depending on what the phone is doing — the dial pad, a call coming in,
 * a call going out, a call up. It opens itself when a call comes in,
 * because a ringing phone nobody can see is a missed call.
 *
 * Mounted once, in `AppNav`, so the registration survives moving between
 * pages: a phone that re-registered on every navigation would miss every
 * call that arrived while it did.
 *
 * The audio element is here and hidden: remote media has to play through
 * something in the document, and the stack is handed a way to find it
 * rather than a reference it would have to hold before the element
 * exists.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button } from "./ui/Button";
import { MicIcon, MicOffIcon, PhoneIcon, PhoneOffIcon, GridIcon } from "./ui/icons";
import { useDismissable } from "../lib/use-dismissable";
import { createSipJsStack, type SipStack } from "../lib/sip-stack";
import { useSoftphone, type SoftphoneState, type UseSoftphoneOptions } from "../lib/use-softphone";
import field from "./ui/field.module.css";
import styles from "./Softphone.module.css";

const DTMF_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

export interface SoftphoneProps {
  /** The SIP side. Defaults to SIP.js against the hidden audio element. */
  stack?: SipStack;
  /** The credential read. Defaults to the API. */
  load?: UseSoftphoneOptions["load"];
  ringer?: UseSoftphoneOptions["ringer"];
}

export function Softphone({ stack, load, ringer }: SoftphoneProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const stackRef = useRef<SipStack | null>(stack ?? null);
  if (!stackRef.current) stackRef.current = createSipJsStack(() => audioRef.current);

  const [state, actions] = useSoftphone({ stack: stackRef.current, load, ringer });
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    onEscape: () => {
      setOpen(false);
      triggerRef.current?.focus();
    },
    pointer: [containerRef],
  });

  // A call coming in opens the panel. Only on the transition, so a person
  // who closed it during a call is not fought with.
  const incoming = state.call?.direction === "inbound" && state.call.state === "ringing";
  useEffect(() => {
    if (incoming) setOpen(true);
  }, [incoming]);

  if (state.status === "off") {
    // No voice server here. The bar carries nothing rather than a dead
    // handset; the audio element stays so the stack has nowhere to fail.
    return <audio ref={audioRef} autoPlay className={styles.audio} />;
  }

  return (
    <div ref={containerRef} className={styles.phone}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={triggerLabel(state)}
        onClick={() => setOpen((o) => !o)}
        className={styles.trigger}
      >
        <PhoneIcon className={styles.triggerIcon} />
        <span className={`${styles.dot} ${styles[dotClass(state)]}`} aria-hidden="true" />
      </button>
      {open && (
        <div role="dialog" aria-label="Phone" className={styles.panel}>
          <SoftphonePanel state={state} actions={actions} />
        </div>
      )}
      <audio ref={audioRef} autoPlay className={styles.audio} />
    </div>
  );
}

function triggerLabel(state: SoftphoneState): string {
  if (state.call?.state === "answered") return "Phone: on a call";
  if (state.call?.state === "ringing") return state.call.direction === "inbound" ? "Phone: incoming call" : "Phone: calling";
  switch (state.status) {
    case "ready":
      return "Phone: ready";
    case "connecting":
      return "Phone: connecting";
    default:
      return "Phone: not working";
  }
}

function dotClass(state: SoftphoneState): "dotReady" | "dotBusy" | "dotWaiting" | "dotError" {
  if (state.call && state.call.state !== "ended") return "dotBusy";
  switch (state.status) {
    case "ready":
      return "dotReady";
    case "connecting":
      return "dotWaiting";
    default:
      return "dotError";
  }
}

interface PanelProps {
  state: SoftphoneState;
  actions: ReturnType<typeof useSoftphone>[1];
}

function SoftphonePanel({ state, actions }: PanelProps) {
  const { call } = state;
  if (call && call.state === "ringing" && call.direction === "inbound") {
    return (
      <div className={styles.screen}>
        <p className={styles.caption}>Incoming call</p>
        <p className={styles.remote}>{call.remote}</p>
        <div className={styles.actions}>
          <Button variant="primary" size="sm" onClick={() => void actions.answer()}>
            Answer
          </Button>
          <Button variant="danger" size="sm" onClick={() => void actions.hangup()}>
            Decline
          </Button>
        </div>
      </div>
    );
  }
  if (call && call.state === "ringing") {
    return (
      <div className={styles.screen}>
        <p className={styles.caption}>Calling</p>
        <p className={styles.remote}>{call.remote}</p>
        <div className={styles.actions}>
          <Button variant="danger" size="sm" onClick={() => void actions.hangup()}>
            Hang up
          </Button>
        </div>
      </div>
    );
  }
  if (call && call.state === "answered") {
    return <InCall call={call} actions={actions} />;
  }
  if (call && call.state === "ended") {
    return (
      <div className={styles.screen}>
        <p className={styles.caption}>{call.endReason || "Call ended"}</p>
        <p className={styles.remote}>{call.remote}</p>
        <div className={styles.actions}>
          <Button variant="secondary" size="sm" onClick={actions.clearEnded}>
            Done
          </Button>
        </div>
      </div>
    );
  }
  return <DialPad state={state} actions={actions} />;
}

function DialPad({ state, actions }: PanelProps) {
  const [number, setNumber] = useState("");
  const [placing, setPlacing] = useState(false);
  const ready = state.status === "ready";

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready || !number.trim()) return;
    setPlacing(true);
    void actions.dial(number).finally(() => {
      setPlacing(false);
      setNumber("");
    });
  };

  return (
    <form className={styles.screen} onSubmit={submit}>
      <p className={styles.caption}>{statusLine(state)}</p>
      <label className={field.label} htmlFor="softphone-number">
        Number
      </label>
      <input
        id="softphone-number"
        className={field.input}
        type="tel"
        inputMode="tel"
        autoComplete="off"
        value={number}
        onChange={(e) => setNumber(e.target.value)}
        disabled={!ready}
      />
      <div className={styles.actions}>
        <Button type="submit" variant="primary" size="sm" disabled={!ready || !number.trim()} loading={placing}>
          Call
        </Button>
        {state.status === "error" && (
          <Button variant="secondary" size="sm" onClick={actions.retry}>
            Try again
          </Button>
        )}
      </div>
    </form>
  );
}

function statusLine(state: SoftphoneState): string {
  switch (state.status) {
    case "ready":
      return "Ready";
    case "connecting":
      return state.message ? `${state.message}. Reconnecting` : "Connecting";
    case "error":
      return state.message || "The phone is not working";
    default:
      return "";
  }
}

function InCall({ call, actions }: { call: NonNullable<SoftphoneState["call"]>; actions: PanelProps["actions"] }) {
  const [keypad, setKeypad] = useState(false);
  const elapsed = useElapsed(call.answeredAt);
  return (
    <div className={styles.screen}>
      <p className={styles.caption}>On a call</p>
      <p className={styles.remote}>{call.remote}</p>
      <p className={styles.duration}>{formatDuration(elapsed)}</p>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.iconButton}
          aria-pressed={call.muted}
          aria-label={call.muted ? "Unmute" : "Mute"}
          onClick={actions.toggleMute}
        >
          {call.muted ? <MicOffIcon className={styles.icon} /> : <MicIcon className={styles.icon} />}
        </button>
        <button
          type="button"
          className={styles.iconButton}
          aria-pressed={keypad}
          aria-label="Keypad"
          onClick={() => setKeypad((k) => !k)}
        >
          <GridIcon className={styles.icon} />
        </button>
        <button type="button" className={styles.hangup} aria-label="Hang up" onClick={() => void actions.hangup()}>
          <PhoneOffIcon className={styles.icon} />
        </button>
      </div>
      {keypad && (
        <div className={styles.keypad} role="group" aria-label="Keypad">
          {DTMF_KEYS.map((k) => (
            <button key={k} type="button" className={styles.key} onClick={() => actions.sendDTMF(k)}>
              {k}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function useElapsed(since: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (since === null) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [since]);
  return since === null ? 0 : Math.max(0, Math.floor((now - since) / 1000));
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
